import moment from "moment";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import User from "../passapp-sdk/user";
import { dataSetConfig, saveUser } from "../redux/actions/dataAction";
import {
  fillListPeopleInside,
  loadingScanPeopleInside,
  loadingSearchPeopleInside,
  loadingSelectingPeopleInside,
  setPageCount,
} from "../redux/actions/registerExitActions";
import {
  convertAuthorization,
  isResident,
} from "../store/functions/userFunction";
import {
  AuthorizationType,
  IAuthorization,
  ISearchUser,
} from "../store/types/userTypes";
import useProviderAuthorization from "./useProviderAuthorization";
import useSdk from "./useSdk";
import { useStore } from "./useStore";
import useTabs from "./useTabs";
import useValidation from "./useValidation";
import { LIMIT_PEOPLEINSIDE } from "../utils/constants/tableConstants";

const userSDK = new User();
const DAYS_TO_SEARCH = 1;

const useRegisterExit = () => {
  const { authStore } = useStore();
  const dispatch = useDispatch();
  const { user, searchUser } = useSdk();
  const { getProfessionTypeById } = useProviderAuthorization();
  const { addTab, autoChangeTab } = useTabs();
  const { isLoadUserTab } = useValidation();

  const getAuthorizations = (event: any) => {
    let authorizations: IAuthorization[] = [];
    if (event?.hostConfirmations?.lenght !== 0) {
      event?.hostConfirmations?.map((item: any) => {
        authorizations.push(
          convertAuthorization(
            { host: item, id: item.id, type: "resident", entriesAsProvider: event?.entriesAsProvider },
            { type: AuthorizationType.current, mark: true }
          ),
        );
      });
    }
    event?.invites?.map((item: any) => {
      authorizations.push(
        convertAuthorization({...item, entriesAsProvider: event?.entriesAsProvider}, {
          type: AuthorizationType.current,
          mark: true,
        }),
      );
    });
    event?.authorizations?.map((item: any) => {
      if (item.providerType)
        item.providerType = getProfessionTypeById(item.providerType);
      authorizations.push(
        convertAuthorization({...item, entriesAsProvider: event?.entriesAsProvider}, {
          type: AuthorizationType.current,
          mark: true,
        }),
      );
    });
    event?.lots?.forEach((item: any) => {
      authorizations.push(
        convertAuthorization(
          {
            host: {
              id: item.id,
              customLot: item.name,
              residencesInfo: [
                {
                  lotInfo: {
                    id: item.id,
                    name: item.name,
                    description: item.description,
                    phone: item.phone,
                  },
                },
              ],
            },
            id: item.id,
            entriesAsProvider: event?.entriesAsProvider
          },
          {
            type: AuthorizationType.authorizedOnGuard,
            mark: true,
          },
        ),
      );
    });
    return authorizations;
  };

  const loadVehicle = (user: any, vehicle: any) => {
    if (!user.isDriver) {
      let vehicles: any[] = [];
      vehicles.push(vehicle);
      user = {
        ...user,
        vehicles,
      };
    }
    return user;
  };

  const fetchPeopleInside = (page?: number, search?: string, sortBy?: string, sortDirection?: 'asc' | 'desc') => {
    dispatch(loadingSearchPeopleInside(true));
    userSDK
      .getPeopleInside(
        authStore.token,
        moment().subtract(DAYS_TO_SEARCH, "day").unix(),
        moment().unix(),
        page,
        LIMIT_PEOPLEINSIDE,
        search,
        sortBy,
        sortDirection
      )
      .then((response: any) => {
        dispatch(fillListPeopleInside(response.data.data));
        dispatch(setPageCount(response.data.total));
      })
      .finally(() => {
        dispatch(loadingSearchPeopleInside(false));
      });
  };

  useEffect(() => {
    if (user) {
      openRegister(user);
    }
  }, [user]);

  const searchExit = (search: ISearchUser) => {
    dispatch(loadingSelectingPeopleInside(true));
    searchUser(search);
  };

  const openRegister = (userSelected: any) => {
    isLoadUserTab({ qr: userSelected.id }).then((response) => {
      if (response.isLoad) {
        autoChangeTab(response.tabIndex);
      } else {
        dispatch(loadingSelectingPeopleInside(true));
        userSDK
          .getLastEntrance(
            authStore.token,
            userSelected.id,
            userSelected.dni,
            userSelected.countryCode,
            false,
          )
          .then((response: any) => {
            const event = userSelected.event || response.data.event;
            const authorizations = getAuthorizations(event);
            if (
              isResident(userSelected) &&
              !authorizations.some((item) => item.id === userSelected.id)
            ) {
              authorizations.push(
                convertAuthorization(
                  { host: userSelected, id: userSelected.id, type: "resident" },
                  { type: AuthorizationType.current, mark: false },
                ),
              );
            }
            if (authorizations.length)
              userSelected = { ...userSelected, authorizations };
            if (event?.photos) {
              userSelected = {
                ...userSelected,
                ...event.photos,
              };
            }
            if (event?.vehicle) {
              userSelected = {
                ...userSelected,
                vehicleSelected: event?.vehicle,
              };
            }
            userSelected = {
              ...userSelected,
              vehicleType: event?.vehicleType || "",
              comment: event?.comment || "",
              permanentComment: event?.peopleComment || "",
              underagedCount: event?.underagedCount || "",
              isDriver: !response.data.event?.driver,
            };
            if (event)
              userSelected = loadVehicle(
                userSelected,
                response.data.event.vehicle,
              );
            if (response.data.relatedEvents?.length > 0) {
              let companions: any[] = [];
              response.data.relatedEvents.forEach((event: any) => {
                let companion = event.who;
                const authorizations = getAuthorizations(event);
                if (authorizations)
                  companion = { ...companion, authorizations };
                if (event?.photos) {
                  companion = {
                    ...companion,
                    ...event.photos,
                  };
                }
                if (event?.vehicle) {
                  companion = {
                    ...companion,
                    vehicleSelected: event?.vehicle,
                  };
                }
                companion = {
                  ...companion,
                  vehicleType: event.vehicleType,
                  comment: event.comment || "",
                  underagedCount: event.underagedCount,
                  isDriver: !event.driver,
                };
                if (event) companion = loadVehicle(companion, event.vehicle);
                companions.push(companion);
              });
              userSelected = {
                ...userSelected,
                companions,
              };
            }
            dispatch(saveUser(userSelected));
            dispatch(
              dataSetConfig({
                eventType: "out",
                newEventOut: !event,
              })
            );
            if (userSelected.companions?.length > 0) {
              addTab(userSelected.dni, true);
            } else {
              addTab(userSelected.dni);
            }
          })
          .finally(() => {
            dispatch(loadingScanPeopleInside(false));
            dispatch(loadingSearchPeopleInside(false));
            dispatch(loadingSelectingPeopleInside(false));
          });
      }
    });
  };

  return {
    openRegister,
    searchExit,
    fetchPeopleInside,
  };
};

export default useRegisterExit;
